

import { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  Tabs,
  Tab,
  Typography,
  Tooltip,
  Stack,
  Drawer,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import RestAPIService from "../../../Utility/Services/RestAPIService";
import ToastMsg from '../../../Components/ToastMsg/ToastMsg';
import { Serviceurls } from '../../.././Utility/API/Serviceurls';
import { CommonGridToolBarWithFilterText, NewCommonPatientDetails, ReasonPopupWithRefund } from "../../../Components/Common Components/CommonComponents";
import { ContentInTooltip, formatDate, formatDateAndTimeMeth2, formatDateTime } from "../../../Components/CommonFunctions/CommonFunctions";
import { ImagePaths } from "../../../Utility/ImagePaths";
import CommonDatePicker from "../../../Components/Common Components/CommonDatePicker";
import { DateTime } from "luxon";
import Loader from '../../../Components/Loader';

const ReverseFinalBill = () => {
  const [tabValue, setTabValue] = useState(0);
  // Current tab states
  const [currentUhid, setCurrentUhid] = useState('');
  const [currentIpNumber, setCurrentIpNumber] = useState('');

  // View Log tab states
  const [logUhid, setLogUhid] = useState('');
  const [logIpNumber, setLogIpNumber] = useState('');

  //Data
  const [data, setData] = useState([]);
  const [logData, setLogData] = useState([]);
  const [logLoaded, setLogLoaded] = useState(false);

  // Toast message states
  const [isErrorMsg, setIsErrorMsg] = useState(false);
  const [isErrorMsgText, setIsErrorMsgText] = useState('');
  const [successMsg, setSuccessMsg] = useState(false);
  const [successMsgText, setSuccessMsgText] = useState('');


  //Pagination
  const [page, setpage] = useState(0);
  const [pageSize, setpageSize] = useState(10);

  //dateFilter
  const [FromDate, setFromDate] = useState('');
  const [ToDate, setToDate] = useState('');
  const [DateIndex, setDateIndex] = useState('');

  //loader
  const [isLoading, setIsLoading] = useState(false)
  const [selectedData, setSelectedData] = useState(null)

  // --- NEW: Ref to track mounted status
  const isMounted = useRef(true);

  // Function to close toast messages
  const msgClose = () => {
    setIsErrorMsg(false);
    setIsErrorMsgText('');
    setSuccessMsg(false);
    setSuccessMsgText('');
  };

  // Helper function to show error toast
  const showErrorToast = (message) => {
    if (isMounted.current) {
      setIsErrorMsgText(message);
      setIsErrorMsg(true);
      setSuccessMsg(false);
    }
  };

  // Helper function to show success toast
  const showSuccessToast = (message) => {
    if (isMounted.current) {
      setSuccessMsgText(message);
      setSuccessMsg(true);
      setIsErrorMsg(false);
    }
  };


  // Drawer state management
  const [filterOpen, setFilterOpen] = useState(false);

  // Drawer handlers
  const handleOpenFilter = () => setFilterOpen(true);
  const handleCloseFilter = () => {
    setFilterOpen(false)
    setSelectedData(null)
  };

  useEffect(() => {
    fetchCurrentData();
    return () => {
      isMounted.current = false;
    };
  }, []);


  const fetchCurrentData = async (params = {}) => {
    let { ip_number, patient_account_number } = params;
    msgClose();
    setIsLoading(true)
    try {
      const response = await RestAPIService.getAll(Serviceurls.CURRENT_API_URL_FB + `?uhid=${patient_account_number ? patient_account_number : ""}&ip_number=${ip_number ? ip_number : ""}`);//+ "?uhid="+patient_account_number&"?ip_number"+ip_admisssion_id
      if (!isMounted.current) return;
      if (response.data.status === 'success') {
        setData(response.data.data || []);
        setSelectedData(null)
        setFilterOpen(false)
      } else {
        showErrorToast(response.data.message);
        setData([]);
      }
    } catch (e) {
      if (!isMounted.current) return;
      showErrorToast(e.message);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLogData = async (params = {}) => {
    let { ip_numberL, patient_account_numberL } = params;
    msgClose();
    setIsLoading(true);
    try {
      const response = await RestAPIService.getAll(Serviceurls.LOG_API_URL_FB + `?uhid=${patient_account_numberL ? patient_account_numberL : ""}&ip_number=${ip_numberL ? ip_numberL : ""}&from_date=${FromDate || ""}&to_date=${ToDate || ""}`);
      if (!isMounted.current) return;
      if (response.data.status === 'success') {
        setLogData(response.data.data || []);
        setFilterOpen(false)
      } else {
        showErrorToast(response.data.message);
        setLogData([]);
      }
      setLogLoaded(true);
    } catch (e) {
      if (!isMounted.current) return;
      showErrorToast(e.message);
      setLogData([]);
      setLogLoaded(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (e, newValue) => {
    setTabValue(newValue);
    // loader (If you have a loader state, you would set it here)
  };

  useEffect(() => {
    if (tabValue === 1 && !logLoaded) {
      fetchLogData();
    }
    // When switching tabs, clear search fields for the other tab
    if (tabValue === 0) {
      setLogUhid('');
      setLogIpNumber('');
      setLogLoaded(false);
    } else { // tabValue === 1
      setCurrentUhid('');
      setCurrentIpNumber('');
    }
  }, [tabValue, logLoaded]);

  const handleSearch = async () => {
    try {
      let params = {
        //CURRENT TAB
        patient_account_number: currentUhid.trim(),
        ip_number: currentIpNumber.trim(),
        //LOG TAB
        patient_account_numberL: logUhid.trim(),
        ip_numberL: logIpNumber.trim()
      };
      if (tabValue === 0) {
        await fetchCurrentData(params);
      } else {
        await fetchLogData(params);
      }
    } catch (err) {
      if (!isMounted.current) return;
      showErrorToast(err.message);
      if (tabValue === 0) setData([]);
      else setLogData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // --- UPDATED FUNCTION TO HANDLE REVERSE DISCHARGE ---
  const handlerevertFinalBill = async (datas) => {

    setIsLoading(true);
    let payment = []
    if (datas?.isCash) {
      payment.push(1)
    }
    if (datas?.isUPI) {
      payment.push(3)
    }
    if (datas?.isBankTranfer) {
      payment.push(5)
    }
    let data = {
      'revert_id': selectedData?.revert_id || null,
      'ip_admission_id': selectedData?.ip_admission_id || null,
      "total_amount": +datas?.totalPayableAmount || 0,
      "cash_amount": +datas?.CashAmount || 0,
      "upi_amount": +datas?.UPIAmount || 0,
      "bank_amount": +datas?.BankTfrAmount || 0,
      "payment_mode": payment,
      "upi_ref_no": datas?.UPINumber || "",
      "bank_ref_no": datas?.BankTfrNumber || "",
      "remarks": datas?.comments || "",
      "receipt_for": datas?.receiptType === "Refund" ? "Refund" : "Cancelled",
    }
    try {
      const response = await RestAPIService.create(data, Serviceurls.REVERT_FINAL_URL);
      if (!isMounted.current) return;
      if (response.data.status === "success") {
        showSuccessToast(response?.data?.message)
        fetchCurrentData({}, true);
      } else {
        showErrorToast(response.data.message);
      }
    } catch (error) {
      if (!isMounted.current) return;
      showErrorToast(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateChange = (fromDate, toDate, dateIndex) => {
    const today = DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd');
    setFromDate(dateIndex === 1 ? today : fromDate);
    setToDate(dateIndex === 1 ? today : toDate);
    setDateIndex(dateIndex);
  };

  const gridToolBar = () => {
    return (
      <Box>
        <CommonGridToolBarWithFilterText />
      </Box>
    )
  }

  const Filter = () => {
    return (
      <Box component={"div"} className='eMed_Filter_Billing_Container'>
        <Box component={"div"} className='eMed_Filter_Top'>
          <Typography variant='h6' fontWeight={"600"}>{"Filter"}</Typography>
          <Button onClick={() => { handleCloseFilter() }} className='eMed_usrconf_btn'>
            <img src={ImagePaths.LabCloseButton.default} alt="close" className='eMed_action_img_small' />
          </Button>
        </Box>
        <br></br>
        <Box component={"div"} >
          {tabValue === 0 ? (
            <Box display="flex" gap={2} mb={2}>
              <TextField
                label="UHID"
                size="small"
                value={currentUhid}
                onChange={(e) => setCurrentUhid(e.target.value)}
                disabled={isLoading}
                sx={{ marginLeft: '1vw' }}
              />
              <TextField
                label="IP Number"
                size="small"
                value={currentIpNumber}
                onChange={(e) => setCurrentIpNumber(e.target.value)}
                disabled={isLoading}
              />
            </Box>
          ) : (
            <Box display="flex" flexDirection={'column'} justifyContent={'flex-end'} gap={2} mb={2} alignItems={'flex-end'} marginRight={"1vw"}>
              <Box style={{ width: "40%", }} component={"div"}>
                <CommonDatePicker
                  FromDate={FromDate}
                  ToDate={ToDate}
                  DateIndex={DateIndex}
                  SelectedDates={handleDateChange}
                />
              </Box>
              <Box display="flex" flexDirection={'row'} gap={2} mb={2} sx={{ marginLeft: '1vw' }}>
                <TextField
                  label="UHID"
                  size="small"
                  value={logUhid}
                  onChange={(e) => setLogUhid(e.target.value)}
                  disabled={isLoading}
                />
                <TextField
                  label="IP Number"
                  size="small"
                  value={logIpNumber}
                  onChange={(e) => setLogIpNumber(e.target.value)}
                  disabled={isLoading}
                />
              </Box>
            </Box>
          )}

          {/* clear and search button */}
          <Box component={"div"} className="eMed_Filter_Btn_Div">
            <Stack direction="row" spacing={2} mt={2}>
              <Button variant="outlined" onClick={() => {
                setLogUhid('');
                setLogIpNumber('');
                msgClose();
                setLogLoaded(false);
                // fetchLogData();
                setCurrentUhid('');
                setCurrentIpNumber('');
                msgClose();
                fetchCurrentData();
              }} disabled={isLoading}>
                CLEAR
              </Button>
              <Button variant="contained" onClick={handleSearch} disabled={isLoading}>
                SEARCH
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>
    )
  };

  const columnsCurrent = [
    {
      field: "patient_name",
      headerName: "Patient Details",
      flex: 0.22,
      renderCell: (params) => {
        return (
          <NewCommonPatientDetails data={params.row} />
        );
      },
    },

    {
      field: "ip_number", flex: 0.16, headerName: "IP Number", headerAlign: "center", align: "center",
      renderCell: (params) => (<Box component={'div'}>
        {params?.row?.ip_number ? params?.row?.ip_number?.length > 22 ?
          <Tooltip placement='top' title={params?.row?.ip_number} arrow>
            <div>{"..." + params?.row?.ip_number.slice(5)}</div></Tooltip> :
          params?.row?.ip_number : "-"}</Box>)
    },
    {
      field: "discharge_date", headerName: "Discharged on", flex: 0.16,
      renderCell: (params) => {
        const dischargeDate = formatDate(params.row.discharge_date) || '-';
        return (
          <Box display="flex" alignItems="center" gap={1} justifyContent={"center"}>
            <Box>
              <Typography >{dischargeDate} </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "created_by_name", headerName: "Request Details", flex: 0.16,
      renderCell: (params) => (
        <Box>
          <Typography fontSize={"0.8vw"} fontWeight={600}>{
            <div style={{ fontSize: '1vw' }}>{params?.row?.created_user?.created_by_name?.length > 15 ?
              <Tooltip placement="top" title={params?.row?.created_user?.created_by_name} arrow><div>{params?.row?.created_user?.created_by_name.slice(0, 15) + "..."}</div></Tooltip>
              : params?.row?.created_user?.created_by_name || "-"}</div>
          }</Typography>
          <Typography fontSize={"0.8vw"}>{(params?.row?.created_date ? formatDateAndTimeMeth2(params?.row?.created_date) : "-")}</Typography>
        </Box>)
    },
    {
      field: "remarks", headerName: "Reason", flex: 0.16,
      renderCell: (params) => {
        const { remarks } = params.row || {};
        return (
          <Tooltip title={remarks || '-'} placement="top" arrow>
            <Box display="flex" alignItems="center" gap={1} justifyContent={"center"}>
              <Box>
                <Typography >{ContentInTooltip(remarks, 15) || '-'} </Typography>
              </Box>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      headerAlign: "center",
      align: "center",
      flex: 0.10,
      renderCell: (params) => (
        <Button
          size="small"
          variant="contained"
          onClick={() => setSelectedData(params.row)}
          disabled={isLoading}
          justifyContent={"center"}
        >
          Revert
        </Button>
      ),
    },
  ];

  const columnsLog = [
    {
      field: "patient_name",
      headerName: "Patient Details",
      flex: 0.18,
      renderCell: (params) => {
        return (
          <NewCommonPatientDetails data={params.row} />
        );
      },
    },

    {
      field: "ip_number", flex: 0.14, headerName: "IP Number", headerAlign: "center", align: "center",
      renderCell: (params) => (<Box component={'div'}>
        {params?.row?.ip_number ? params?.row?.ip_number?.length > 17 ?
          <Tooltip placement='top' title={params?.row?.ip_number} arrow>
            <div>{"..." + params?.row?.ip_number.slice(5)}</div></Tooltip> :
          params?.row?.ip_number : "-"}</Box>)
    },
    {
      field: "discharge_date", headerName: "Discharged on", flex: 0.14,
      renderCell: (params) => {
        const dischargeDate = formatDate(params.row.discharge_date) || '-';
        return (
          <Box display="flex" alignItems="center" gap={1} justifyContent={"center"}>
            <Box>
              <Typography>{dischargeDate} </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "remarks", headerName: "Reason", flex: 0.14,
      renderCell: (params) => {
        const { remarks } = params.row || {};
        return (
          <Box display="flex" alignItems="center" gap={1} justifyContent={"center"}>
            <Box>
              <Typography >{ContentInTooltip(remarks, 15) || '-'} </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "created_at", headerName: "Request Details", flex: 0.13,
      renderCell: (params) => (
        <Box>
          <Typography fontSize={"0.8vw"} fontWeight={600}>{
            <div style={{ fontSize: '0.8vw' }}>{params?.row?.created_by?.length > 15 ?
              <Tooltip placement="top" title={params?.row?.created_by} arrow><div>{params?.row?.created_by.slice(0, 15) + "..."}</div></Tooltip>
              : params?.row?.created_by || "-"}</div>
          }</Typography>
          <Typography fontSize={"0.8vw"}>{(params?.row?.approved_date ? formatDateAndTimeMeth2(params?.row?.approved_date) : "-")}</Typography>
        </Box>)
    },
    {
      field: "approved_by", headerName: "Approver Details", flex: 0.13,
      renderCell: (params) => (
        <Box>
          <Typography fontSize={"0.8vw"} fontWeight={600}>{
            <div style={{ fontSize: '0.8vw' }}>{params?.row?.approved_by?.length > 15 ?
              <Tooltip placement="top" title={params?.row?.approved_by} arrow><div>{params?.row?.approved_by.slice(0, 15) + "..."}</div></Tooltip>
              : params?.row?.approved_by || "-"}</div>
          }</Typography>
          <Typography fontSize={"0.8vw"}>{(params?.row?.approved_date ? formatDateAndTimeMeth2(params?.row?.approved_date) : "-")}</Typography>
        </Box>)
    },
  ];



  return (
    <Box>
      {/* Toast Messages */}
      {isErrorMsg ?
        <ToastMsg
          severity={'error'}
          msg={isErrorMsgText}
          msgPop={msgClose} /> : null}

      {!isErrorMsg && successMsg && successMsgText !== '' &&
        <ToastMsg
          severity={'success'}
          msg={successMsgText}
          msgPop={msgClose} />}

      <Box display={'flex'} justifyContent={'space-between'}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Pending" disabled={isLoading} sx={{ textTransform: 'capitalize', minHeight: '1vw', fontWeight: 600 }} />
          <Tab label="Completed" disabled={isLoading} sx={{ textTransform: 'capitalize', minHeight: '1vw', fontWeight: 600 }} />
        </Tabs>
        <Box component="div" className="eMed_Filter_Icon_div" sx={{ marginTop: "1dvh" }}>
          <Tooltip title="Filter" placement="top" arrow>
            <Button className="eMed_usrconf_btn" onClick={handleOpenFilter}>
              <img src={ImagePaths.Filter.default} alt="filter" className="eMed_action_img" />
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <CircularProgress />
          <Typography ml={2}>Loading data...</Typography>
        </Box>
      ) : ( */}
      <Loader loaderOpen={isLoading} />
      <div style={{ height: '72dvh', width: '100%' }}>
        <DataGrid
          rows={tabValue === 0 ? data : logData}
          columns={tabValue === 0 ? columnsCurrent : columnsLog}
          getRowId={(row) => row.revert_id || row.id || row.ip_admission_id || (row.uhid + row.ip_number) || JSON.stringify(row)}
          getRowHeight={() => 70}
          density="compact"
          onPageChange={(newPage) => { setpage(newPage) }}
          onPageSizeChange={(newPageSize) => { setpageSize(newPageSize) }}
          rowsPerPageOptions={[10, 20, 30]}
          page={page}
          pageSize={pageSize}
          localeText={{
            toolbarColumns: "",
            toolbarDensity: "",
            toolbarExport: "",
            toolbarFilters: "",
            toolbarExportPrint: ""
          }}
          components={{
            Toolbar: gridToolBar,
            NoRowsOverlay: () => (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                {'No Records To Be Shown'}
              </Box>
            )
          }}
        />
      </div>

      <Drawer
        anchor="right"
        open={filterOpen}
        onClose={handleCloseFilter}
      >
        {Filter()}
      </Drawer>
      {selectedData ?
        <ReasonPopupWithRefund
          title={"Are you sure? Do you want to revert the final bill?"}
          AlertPopupClose={handleCloseFilter}
          label={"Enter the Revert Reason"}
          btntext={'Close'}
          btnvarient={'outlined'}
          btncolor={'error'}
          btntext1={'Confirm'}
          btnvarient1={'contained'}
          RefundPost={handlerevertFinalBill.bind(this)}
          BillReceivedAmount={selectedData?.fo_paid_receipt || 0}
          payableAmount={selectedData?.fo_paid_receipt || 0}
          AdjectedAdvance={selectedData?.advance_available_receipt || 0}
          RevertFinal={true} />
        : null}
    </Box>
  );
};

export default ReverseFinalBill;